import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
  Box,
  Typography,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Add as AddIcon,
  Edit,
  Delete,
  Save,
  Cancel
} from '@mui/icons-material';
import { addressService, customerService } from '../services/customerService';
import { formatCustomerAddressType } from '../utils/formatCustomerAddressType';

function normalizeAddressList(payload) {
  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload.data)) return payload.data;
  return [];
}

function customerHasProfileAddress(c) {
  if (!c) return false;
  return Boolean(
    String(c.address_line1 || '').trim() ||
      String(c.address_line2 || '').trim() ||
      String(c.city || '').trim() ||
      String(c.state || '').trim() ||
      String(c.zip_code || '').trim()
  );
}

const CustomerAddressesModal = ({ open, onClose, customerId, customerName }) => {
  const [addresses, setAddresses] = useState([]);
  const [customerRecord, setCustomerRecord] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [addressToDelete, setAddressToDelete] = useState(null);
  const [formData, setFormData] = useState({
    address_type: 'shipping',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'United States',
    is_default: false,
    notes: ''
  });

  useEffect(() => {
    if (open && customerId) {
      loadAddresses();
    } else {
      setAddresses([]);
      setCustomerRecord(null);
      setEditingId(null);
      resetForm();
    }
  }, [open, customerId]);

  const loadAddresses = async () => {
    try {
      setLoading(true);
      setError('');
      const [addrPayload, custPayload] = await Promise.all([
        addressService.getByCustomerId(customerId),
        customerService.getById(customerId).catch(() => null)
      ]);
      setAddresses(normalizeAddressList(addrPayload));
      const cust = custPayload && (custPayload.data != null ? custPayload.data : custPayload);
      setCustomerRecord(cust && typeof cust === 'object' ? cust : null);
    } catch (err) {
      console.error('Error loading addresses:', err);
      setError(err.response?.data?.message || 'Failed to load addresses');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyProfileToAddressBook = () => {
    if (!customerRecord) return;
    setFormData({
      address_type: 'billing',
      address_line1: customerRecord.address_line1 || '',
      address_line2: customerRecord.address_line2 || '',
      city: customerRecord.city || '',
      state: customerRecord.state || '',
      zip_code: customerRecord.zip_code || '',
      country: customerRecord.country || 'United States',
      is_default: addresses.length === 0,
      notes: 'Copied from customer profile'
    });
    setEditingId('new');
  };

  const resetForm = () => {
    setFormData({
      address_type: 'shipping',
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      zip_code: '',
      country: 'United States',
      is_default: false,
      notes: ''
    });
    setEditingId(null);
    setError('');
  };

  const handleAdd = () => {
    resetForm();
    setEditingId('new');
  };

  const handleEdit = (address) => {
    setFormData({
      address_type: address.address_type || 'shipping',
      address_line1: address.address_line1 || '',
      address_line2: address.address_line2 || '',
      city: address.city || '',
      state: address.state || '',
      zip_code: address.zip_code || '',
      country: address.country || 'United States',
      is_default: address.is_default || false,
      notes: address.notes || ''
    });
    setEditingId(address.id);
  };

  const handleCancel = () => {
    resetForm();
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');

      if (editingId === 'new') {
        await addressService.create(customerId, formData);
      } else {
        await addressService.update(customerId, editingId, formData);
      }

      await loadAddresses();
      resetForm();
    } catch (err) {
      console.error('Error saving address:', err);
      setError(err.response?.data?.message || 'Failed to save address');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (addressId) => {
    setAddressToDelete(addressId);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!addressToDelete) return;
    try {
      await addressService.delete(customerId, addressToDelete);
      await loadAddresses();
      setDeleteDialogOpen(false);
      setAddressToDelete(null);
    } catch (err) {
      console.error('Error deleting address:', err);
      setError(err.response?.data?.message || 'Failed to delete address');
      setDeleteDialogOpen(false);
    }
  };

  const handleInputChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [field]: value
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Customer Addresses - {customerName}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 1.5, py: 0.5 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {customerHasProfileAddress(customerRecord) && (
              <Alert severity="info" sx={{ mb: 1.5, py: 0.75 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Main address (customer profile)
                </Typography>
                <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>
                  {[
                    customerRecord.address_line1,
                    customerRecord.address_line2,
                    [customerRecord.city, customerRecord.state, customerRecord.zip_code].filter(Boolean).join(', '),
                    customerRecord.country &&
                    customerRecord.country !== 'United States' &&
                    customerRecord.country !== 'USA'
                      ? customerRecord.country
                      : ''
                  ]
                    .filter(Boolean)
                    .join('\n')}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  This is stored on the customer record. Quotes and orders can use it when no address-book row is selected. Use{' '}
                  <strong>Add Address</strong> or <strong>Copy to address book</strong> to create rows you can pick from lists.
                </Typography>
                {!editingId && (
                  <Button size="small" variant="outlined" onClick={handleCopyProfileToAddressBook}>
                    Copy to address book
                  </Button>
                )}
              </Alert>
            )}

            {editingId && (
              <Box sx={{ mb: 1.5, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  {editingId === 'new' ? 'Add New Address' : 'Edit Address'}
                </Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth margin="dense" size="small">
                      <InputLabel>Type</InputLabel>
                      <Select
                        value={formData.address_type}
                        label="Type"
                        onChange={handleInputChange('address_type')}
                      >
                        <MenuItem value="billing">Billing</MenuItem>
                        <MenuItem value="shipping">Shipping</MenuItem>
                        <MenuItem value="other">Other</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Address Line 1"
                      value={formData.address_line1}
                      onChange={handleInputChange('address_line1')}
                      margin="dense"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Address Line 2"
                      value={formData.address_line2}
                      onChange={handleInputChange('address_line2')}
                      margin="dense"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="City"
                      value={formData.city}
                      onChange={handleInputChange('city')}
                      margin="dense"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="State"
                      value={formData.state}
                      onChange={handleInputChange('state')}
                      margin="dense"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Zip Code"
                      value={formData.zip_code}
                      onChange={handleInputChange('zip_code')}
                      margin="dense"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Country"
                      value={formData.country}
                      onChange={handleInputChange('country')}
                      margin="dense"
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={(
                        <Checkbox
                          checked={!!formData.is_default}
                          onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                          size="small"
                        />
                      )}
                      label={
                        formData.address_type === 'billing'
                          ? 'Default billing address'
                          : formData.address_type === 'shipping'
                            ? 'Default shipping address'
                            : 'Default address for this type'
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Notes"
                      value={formData.notes}
                      onChange={handleInputChange('notes')}
                      margin="dense"
                      size="small"
                      multiline
                      rows={2}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<Save />}
                        onClick={handleSave}
                        disabled={saving}
                      >
                        Save
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Cancel />}
                        onClick={handleCancel}
                        disabled={saving}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Address book ({addresses.length})
              </Typography>
              {!editingId && (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAdd}
                >
                  Add Address
                </Button>
              )}
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Type</TableCell>
                    <TableCell>Default</TableCell>
                    <TableCell>Address</TableCell>
                    <TableCell>City</TableCell>
                    <TableCell>State</TableCell>
                    <TableCell>Zip</TableCell>
                    <TableCell>Country</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {addresses.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} sx={{ textAlign: 'center', fontStyle: 'italic' }}>
                        {customerHasProfileAddress(customerRecord)
                          ? 'No rows in the address book yet. The main profile address is shown above — use Copy to address book or Add Address.'
                          : 'No addresses in the book. Click "Add Address" to add billing or shipping addresses.'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    addresses.map((address) => (
                      <TableRow key={address.id}>
                        <TableCell>{formatCustomerAddressType(address.address_type)}</TableCell>
                        <TableCell>{address.is_default ? 'Yes' : '—'}</TableCell>
                        <TableCell>
                          {[address.address_line1, address.address_line2].filter(Boolean).join(', ') || '-'}
                        </TableCell>
                        <TableCell>{address.city || '-'}</TableCell>
                        <TableCell>{address.state || '-'}</TableCell>
                        <TableCell>{address.zip_code || '-'}</TableCell>
                        <TableCell>{address.country || 'USA'}</TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleEdit(address)}
                            disabled={editingId !== null}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(address.id)}
                            disabled={editingId !== null}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} size="small">
          Close
        </Button>
      </DialogActions>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setAddressToDelete(null);
        }}
        aria-labelledby="delete-address-dialog-title"
        aria-describedby="delete-address-dialog-description"
      >
        <DialogTitle id="delete-address-dialog-title">
          Delete Address?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-address-dialog-description">
            Are you sure you want to delete this address? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDeleteDialogOpen(false);
            setAddressToDelete(null);
          }} color="primary" size="small">
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained" size="small" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default CustomerAddressesModal;
